import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import PageHeader from "@app/shared/PageHeader";
import PageSection from "@app/shared/PageSection";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { formatDate } from "@app/operations/market-nightly-refresh/utils";
import { MarketNightlyRefreshService } from "@app/operations/market-nightly-refresh/marketNightlyRefreshService";
import { FC, useRef } from "react";
import { MarketNightlyRefreshProviderSet, MarketNightlyRefreshSummary } from "@app/operations/market-nightly-refresh/interfaces";
import { isProviderSetIgnored } from "@app/operations/market-nightly-refresh/MarketNightlyRefreshFilters";
import { ProviderSetTable } from "@app/operations/market-nightly-refresh/ProviderSetTable"
import { useQuery } from "@tanstack/react-query";

export const buildSummary = (providerSets: MarketNightlyRefreshProviderSet[]): MarketNightlyRefreshSummary => {
  const completedSets = providerSets.filter(p => p.status === "Completed");
  const lastRunCompletedAt = completedSets
    .map(p => p.lastRunDate)
    .filter((d): d is string => d !== null)
    .sort()
    .at(-1) ?? null;

  return {
    completedCount: completedSets.length,
    expectedCount: providerSets.length,
    failedCount: providerSets.filter(p => p.status === "Failed").length,
    ignoredCount: providerSets.filter(p => isProviderSetIgnored(p)).length,
    lastRunCompletedAt,
    processingCount: providerSets.filter(p => p.status === "Processing").length,
  };
};

export const MarketNightlyRefresh: FC = () => {
  const service = useRef(new MarketNightlyRefreshService()).current;
  const { data, isLoading } = useQuery({
    queryFn: ({ signal }) => service.getProviderSets(signal),
    queryKey: ["market-nightly-refresh-provider-sets"],
  });
  const providerSets = data ?? [];

  const summary = buildSummary(providerSets);

  return (
    <Page documentTitle="Market Nightly Refresh">
      <Breadcrumbs
        template={BreadcrumbTemplateEnum.Operations}
        items={["Market Nightly Refresh"]} />
      <PageInner>
        <PageHeader title="Market Nightly Refresh" disableHR={true} />
        <PageSection>
          <p className="market-nightly-refresh__description">
            Nightly process that refreshes counts and metadata for every market provider set in QES.
            Use this view to confirm which market provider sets ran overnight, when they ran, and the totals each produced.
          </p>
          {!isLoading && (<RunStatusSummary summary={summary} />)}
          <QESAlert type="info" persist={true}>
            Market provider sets used in <b> 75 or more projects </b> will run automatically each night.
            You can override that logic by selecting a provider set to include or exclude and applying that change.
          </QESAlert>
          <ProviderSetTable isLoading={isLoading} providers={providerSets} />
        </PageSection>
      </PageInner>
    </Page>
  )
}

interface RunStatusSummaryProps {
  summary: MarketNightlyRefreshSummary;
}

export const RunStatusSummary: FC<RunStatusSummaryProps> = ({ summary }) => {
  return (
    <div className="market-nightly-refresh__summary">
      <div className="market-nightly-refresh__stat">
        Last run completed <strong>{summary.lastRunCompletedAt ? formatDate(summary.lastRunCompletedAt) : "-"}</strong>
      </div>
      <div className="market-nightly-refresh__stat">
        <QesIcon iconName={QesIcons.Check} isDecorative={true} />
        <strong>{summary.completedCount} of {summary.expectedCount}</strong>
        <span className="market-nightly-refresh__stat-label market-nightly-refresh__stat-label--completed"> completed</span>
      </div>
      {summary.processingCount > 0 && (
        <div className="market-nightly-refresh__stat">
          <span className="market-nightly-refresh__status-dot market-nightly-refresh__status-dot--processing" />
          <strong>{summary.processingCount}</strong> processing
        </div>
      )}
      {summary.failedCount > 0 && (
        <div className="market-nightly-refresh__stat">
          <span className="market-nightly-refresh__status-dot market-nightly-refresh__status-dot--failed" />
          <strong>{summary.failedCount}</strong> failed
        </div>
      )}
      <div className="market-nightly-refresh__stat">
        <QesIcon iconName={QesIcons.EyeOff} isDecorative={true} />
        <strong>{summary.ignoredCount}</strong> ignored
      </div>
    </div>
  )
}